/// <amd-module name="Release/Landing/PageData" />
import { IVssContributionService } from "VSS/Platform/Contribution";
import { Services, IVssObservableService, VssObservableService } from "VSS/Platform/Context";

import { IReleasesHubServiceData, IActiveDefinitionReference, IActiveDefinitionsDataProviderData, IActiveReleasesData, IDeploymentsListData, ILandingFolderReference } from "Release/Landing/DataProviderContracts";
import { RmServiceInstanceId } from "Constants";
import { Folder } from "Release/Client/Contracts/Release";

const dataProviderId = "ms.vss-releaseManagement-web.release-landing-data";
const activeDefinitionsDataProviderId: string = "ms.vss-releaseManagement-web.active-definitions-data-provider-new";
const serviceType = "IReleaseHubServiceDataService";

export const updateEvent = "data-provider-updated";

export interface IReleaseHubServiceDataService extends IVssObservableService<IReleasesHubServiceData> {
    hasAnyReleaseDefinitions(): boolean | null | undefined;
    getActiveDefinitionReferences(): IActiveDefinitionReference[] | undefined;
    getRecentDefinitionReferences(): IActiveDefinitionReference[] | undefined;
    clearActiveTabDefinitions(): void;
    getReleases(definitionId: number, folderPath?: string): IActiveReleasesData | undefined;
    getDeployments(definitionId: number): IDeploymentsListData | undefined;
    getFolders(): Folder[] | undefined;
    getRootReleaseDefinitions(): IActiveDefinitionReference[] | undefined;
    getSelectedDefinition(): IActiveDefinitionReference | undefined;
    getSelectedFolderPath(): string | undefined;
    getActiveDefinitionReferencesAsync(): Promise<IActiveDefinitionReference[]>;
    getRecentDefinitionReferencesAsync(): Promise<IActiveDefinitionReference[]>;
    getReleasesAsync(definitionId: number, folderPath?: string): Promise<IActiveReleasesData | undefined>;
}

class ReleaseHubServiceDataService extends VssObservableService<IReleasesHubServiceData> implements IReleaseHubServiceDataService {
    public hasAnyReleaseDefinitions(): boolean | null | undefined {
        return this.data && this.data.hasAnyDefinition;
    }

    public getActiveDefinitionReferences(): IActiveDefinitionReference[] | undefined {
        const data = this._data;
        if (data) {
            return data.activeDefinitions;
        }
    }

    public getRecentDefinitionReferences(): IActiveDefinitionReference[] | undefined {
        const data = this._data;
        if (data) {
            return data.recentDefinitions;
        }
    }

    public clearActiveTabDefinitions(): void {
        if (this._data) {
            delete (this._data as Partial<IReleasesHubServiceData>).activeDefinitions;
            delete (this._data as Partial<IReleasesHubServiceData>).recentDefinitions;
        }
    }

    public getReleases(definitionId: number, folderPath?: string): IActiveReleasesData | undefined {

        // Do not make a data provider call if this._data is not present or this._promise is not present
        // We will use other network calls to fetch these if that is the case.
        // DO NOT use this.data, or this.promise. Use the underscored ones (this._data, this._promise)
        const data = this._data;
        if (data) {
            return this._getReleasesFromData(data, definitionId, folderPath);
        };
    }

    public getDeployments(definitionId: number, folderPath?: string): IDeploymentsListData | undefined {
        // Do not make a data provider call if this._data is not present or this._promise is not present
        // We will use other network calls to fetch these if that is the case.
        // DO NOT use this.data, or this.promise. Use the underscored ones (this._data, this._promise)
        const data = this._data;
        if (data) {
            const allDefinitions = [...(data.activeDefinitions || []), ...(data.recentDefinitions || []), ...((data as IReleasesHubServiceData).releaseDefinitions || [])];
            const definition = allDefinitions.find((def) => def.id === definitionId);
            if (definition && definition.deploymentsData) {
                const deploymentsResult = definition.deploymentsData

                definition.deploymentsData = null;
                return deploymentsResult;
            }
        };
    }

    public getFolders(): Folder[] | undefined {
        if (this.data) {
            const folders = this.data.folders;

            const folderWithReleases = (folders || []).find(f => !!f.releasesData && !!f.releasesData.releases);

            if (folderWithReleases && folderWithReleases.releasesData) {
                this._folderReleases[folderWithReleases.path] = folderWithReleases.releasesData;
            }

            // For any new folder additions to root, or other opertions, where we want to re-fetch folders under root,
            // this will be providing stale date. Therefore, set it to undefined
            this.data.folders = undefined;
            return folders;
        }
    }

    public getRootReleaseDefinitions(): IActiveDefinitionReference[] | undefined {
        if (this.data) {
            return this.data.releaseDefinitions;
        }
    }

    public getSelectedDefinition(): IActiveDefinitionReference | undefined {
        const data = this._data;
        if (data) {
            const allDefinitions = [...(data.activeDefinitions || []), ...(data.recentDefinitions || []), ...((data as IReleasesHubServiceData).releaseDefinitions || [])];
            return allDefinitions.find(def => !!def.releasesData || !!def.deploymentsData);
        }
    }

    public getSelectedFolderPath(): string | undefined {
        const folderPathsWithReleases = Object.keys(this._folderReleases);
        if (folderPathsWithReleases.length === 1) {
            return folderPathsWithReleases[0];
        }

        const data = this._data;
        if (data) {
            const folders = data.folders;

            const folderWithReleases = (folders || []).find(f => !!f.releasesData && !!f.releasesData.releases);

            if (folderWithReleases && folderWithReleases.releasesData) {
                return folderWithReleases.path;
            }
            else if (data.rootFolderReleasesData) {
                return "\\"
            }
        }
    }

    public getActiveDefinitionReferencesAsync(): Promise<IActiveDefinitionReference[]> {
        return this.dataPromise.then((data: IReleasesHubServiceData) => data.activeDefinitions);
    }

    public getRecentDefinitionReferencesAsync(): Promise<IActiveDefinitionReference[]> {
        return this.dataPromise.then((data: IReleasesHubServiceData) => data.recentDefinitions);
    }

    public getReleasesAsync(definitionId: number, folderPath?: string): Promise<IActiveReleasesData | undefined> {

        // Do not make a data provider call if this._data is not present or this._promise is not present
        // We will use other network calls to fetch these if that is the case.
        // DO NOT use this.data, or this.promise. Use the underscored ones (this._data, this._promise)
        let promise = this._data ? Promise.resolve(this._data) : this._promise;

        if (!promise) {
            return Promise.resolve(undefined);
        }

        return promise.then((data: IReleasesHubServiceData) => {
            return this._getReleasesFromData(data, definitionId, folderPath);
        });
    }

    private get data(): IReleasesHubServiceData | undefined {
        if (!this._data) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            this._data = contributionService.getData<IReleasesHubServiceData>(dataProviderId);
            if (!this._data) {
                this._setActiveDataPromise();
                this._promise
                    .then((data: IReleasesHubServiceData) => {
                        this._data = data;
                        this._notify(this._data, updateEvent);
                    });
            }
        }

        return this._data;
    }

    private get dataPromise(): Promise<IActiveDefinitionsDataProviderData | IReleasesHubServiceData | undefined> {
        if (!this._promise) {
            if (this.data && this.data.activeDefinitions && this.data.recentDefinitions) {
                this._promise = Promise.resolve(this.data);
            }
            else {
                this._setActiveDataPromise();
            }
        }

        return this._promise;
    }

    private _setActiveDataPromise(): void {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        this._promise = contributionService.getDataAsync<IActiveDefinitionsDataProviderData>(activeDefinitionsDataProviderId, undefined, true, RmServiceInstanceId);
    }

    private _getReleasesFromData(data: IReleasesHubServiceData | IActiveDefinitionsDataProviderData, definitionId: number, folderPath: string | undefined): IActiveReleasesData | undefined {
        if (folderPath === "\\" && (data as IReleasesHubServiceData).rootFolderReleasesData) {
            const releasesHubData = (data as IReleasesHubServiceData);
            const releasesResult = releasesHubData.rootFolderReleasesData;
            releasesHubData.rootFolderReleasesData = undefined;
            return releasesResult;
        }
        else if (folderPath && this._folderReleases[folderPath]) {
            if (this._folderReleases[folderPath]) {
                const releasesResult = this._folderReleases[folderPath]

                delete this._folderReleases[folderPath];
                return releasesResult;
            }
        }
        else {
            const allDefinitions = [...(data.activeDefinitions || []), ...(data.recentDefinitions || []), ...((data as IReleasesHubServiceData).releaseDefinitions || [])];
            const definition = allDefinitions.find((def) => def.id === definitionId);
            if (definition && definition.releasesData) {
                const releasesResult = definition.releasesData

                definition.releasesData = null;
                return releasesResult;
            }
        }
    }

    private _folderReleases: { [path: string]: IActiveReleasesData } = {};
    private _data: IReleasesHubServiceData | undefined;
    private _promise: Promise<IActiveDefinitionsDataProviderData | IReleasesHubServiceData | undefined>;
}

Services.add(serviceType, { serviceFactory: ReleaseHubServiceDataService });
